import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarCheck, Clock, Coins, Repeat2 } from 'lucide-react'

import { payrollApi, teachersApi } from '@/api/endpoints'
import type { BillingPeriod } from '@/api/types'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, StatCard } from '@/components/ui/Card'
import { SelectField } from '@/components/ui/Field'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import {
  formatAmount,
  formatMinutes,
  formatMonthLabel,
  formatRate,
  previousMonth,
} from '@/lib/billing'
import { formatDate } from '@/lib/format'

interface Props {
  okresy: BillingPeriod[]
  isAdmin: boolean
}

export function TeacherSummaryPanel({ okresy, isAdmin }: Props) {
  const { notifySuccess, notifyError } = useToast()
  const queryClient = useQueryClient()

  const [teacherId, setTeacherId] = useState('')
  const [okresIndex, setOkresIndex] = useState(0)

  const teachersQuery = useQuery({
    queryKey: ['teachers', 'picker'],
    queryFn: () => teachersApi.list({ size: 200 }),
    enabled: isAdmin,
  })

  const wybranyNauczyciel = isAdmin ? teacherId || teachersQuery.data?.items[0]?.user_id : undefined
  const okres = okresy[okresIndex]

  const summaryQuery = useQuery({
    queryKey: ['payroll', 'summary', okres?.date_from, okres?.date_to, wybranyNauczyciel ?? 'ja'],
    queryFn: () =>
      payrollApi.summary({
        date_from: okres!.date_from,
        date_to: okres!.date_to,
        teacher_id: wybranyNauczyciel,
      }),
    enabled: Boolean(okres) && (!isAdmin || Boolean(wybranyNauczyciel)),
    placeholderData: keepPreviousData,
  })

  const closuresQuery = useQuery({
    queryKey: ['payroll', 'closures', wybranyNauczyciel ?? 'ja'],
    queryFn: () => payrollApi.closures(wybranyNauczyciel),
  })

  const doZamkniecia = useMemo(() => previousMonth(), [])
  const juzZamkniety = (closuresQuery.data ?? []).some(
    (item) => item.year === doZamkniecia.year && item.month === doZamkniecia.month,
  )

  const zamknij = useMutation({
    mutationFn: () =>
      payrollApi.closeMonth({
        year: doZamkniecia.year,
        month: doZamkniecia.month,
        teacher_id: wybranyNauczyciel,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
      notifySuccess('Miesiąc zamknięty — trafił do raportu wypłat.')
    },
    onError: (error: Error) => notifyError(error.message),
  })

  const dane = summaryQuery.data

  return (
    <div className="space-y-6">
      <Card>
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          {isAdmin && (
            <SelectField
              label="Nauczyciel"
              value={wybranyNauczyciel ?? ''}
              onChange={(event) => setTeacherId(event.target.value)}
              options={(teachersQuery.data?.items ?? []).map((item) => ({
                value: item.user_id,
                label: item.user.full_name,
              }))}
            />
          )}
          <SelectField
            label="Okres"
            value={String(okresIndex)}
            onChange={(event) => setOkresIndex(Number(event.target.value))}
            options={okresy.map((item, index) => ({
              value: String(index),
              label: item.label,
            }))}
          />
        </div>
      </Card>

      {summaryQuery.isPending || !dane ? (
        <Card>
          <SkeletonRows rows={3} />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Lekcje zrealizowane"
              value={String(dane.tally.lessons_held)}
              icon={<CalendarCheck size={20} />}
            />
            <StatCard
              label="Czas płatny"
              value={formatMinutes(dane.tally.payable_minutes)}
              icon={<Clock size={20} />}
              note={`${dane.tally.lessons_payable} lekcji`}
            />
            <StatCard
              label="Wynagrodzenie"
              value={formatAmount(dane.amount)}
              icon={<Coins size={20} />}
              tone="success"
              note={formatRate(dane.hourly_rate)}
            />
            <StatCard
              label="Do odrobienia"
              value={String(dane.tally.makeups_due)}
              icon={<Repeat2 size={20} />}
              tone={dane.tally.makeups_due > 0 ? 'warning' : 'default'}
              note={`${dane.tally.makeups_done} odrobionych`}
            />
          </div>

          {dane.amount === null && dane.tally.lessons_payable > 0 && (
            <Alert tone="warning" title="Brak stawki godzinowej">
              Nauczyciel nie ma ustalonej stawki, więc wynagrodzenia nie da się policzyć. Uzupełnia
              ją administracja w profilu nauczyciela.
            </Alert>
          )}

          <Card>
            <div className="border-b border-outline-variant p-4">
              <h2 className="font-serif text-lg text-on-surface">Lekcje z uczniami</h2>
              <p className="text-sm text-on-surface-variant">
                Liczba zajęć zrealizowanych z każdym uczniem w wybranym okresie.
              </p>
            </div>
            <DataTable>
              <TableHead
                columns={['Uczeń', 'Zrealizowane', 'Obecny', 'Nieobecny', 'Do odrobienia']}
              />
              <TableBody>
                {dane.students.length === 0 ? (
                  <EmptyRow colSpan={5} message="Brak rozliczonych zajęć w tym okresie." />
                ) : (
                  dane.students.map((item) => (
                    <TableRow key={item.student_id}>
                      <TableCell>{item.student_name}</TableCell>
                      <TableCell>{item.tally.lessons_held}</TableCell>
                      <TableCell>{item.tally.present}</TableCell>
                      <TableCell>
                        {item.tally.absent_notified + item.tally.absent_unnotified}
                      </TableCell>
                      <TableCell>{item.tally.makeups_due}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </DataTable>
          </Card>

          {dane.months.length > 1 && (
            <Card>
              <div className="border-b border-outline-variant p-4">
                <h2 className="font-serif text-lg text-on-surface">Miesiąc po miesiącu</h2>
              </div>
              <DataTable>
                <TableHead columns={['Miesiąc', 'Zrealizowane', 'Czas płatny']} />
                <TableBody>
                  {dane.months.map((item) => (
                    <TableRow key={`${item.year}-${item.month}`}>
                      <TableCell>{formatMonthLabel(item)}</TableCell>
                      <TableCell>{item.tally.lessons_held}</TableCell>
                      <TableCell>{formatMinutes(item.tally.payable_minutes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </Card>
          )}
        </>
      )}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline-variant p-4">
          <div>
            <h2 className="font-serif text-lg text-on-surface">Zamknięcie miesiąca</h2>
            <p className="text-sm text-on-surface-variant">
              Zamknięcie zamraża obecności i przekazuje kwotę do raportu wypłat. Późniejsza zmiana
              wymaga odblokowania przez administratora.
            </p>
          </div>
          <Button
            onClick={() => zamknij.mutate()}
            isLoading={zamknij.isPending}
            disabled={juzZamkniety || (isAdmin && !wybranyNauczyciel)}
          >
            {juzZamkniety
              ? `${formatMonthLabel(doZamkniecia)} — zamknięty`
              : `Zamknij ${formatMonthLabel(doZamkniecia)}`}
          </Button>
        </div>

        {closuresQuery.isPending ? (
          <SkeletonRows rows={2} />
        ) : (closuresQuery.data ?? []).length === 0 ? (
          <EmptyState
            icon={<Coins size={28} />}
            title="Brak zamkniętych miesięcy"
            description="Miesiąc można zamknąć po jego zakończeniu i uzupełnieniu wszystkich obecności."
          />
        ) : (
          <DataTable>
            <TableHead
              columns={['Miesiąc', 'Lekcje płatne', 'Czas', 'Stawka', 'Kwota', 'Zamknięto']}
            />
            <TableBody>
              {(closuresQuery.data ?? []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatMonthLabel(item)}</TableCell>
                  <TableCell>{item.lessons_payable}</TableCell>
                  <TableCell>{formatMinutes(item.payable_minutes)}</TableCell>
                  <TableCell>{formatRate(item.hourly_rate)}</TableCell>
                  <TableCell>{formatAmount(item.amount)}</TableCell>
                  <TableCell>{formatDate(item.closed_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Card>
    </div>
  )
}
